/**
 * Google Translate integration.
 *
 * Drives the hidden Google Translate widget programmatically so the
 * custom language dropdown in the Header controls all page translation.
 *
 * HOW IT WORKS
 * 1. index.html loads the Google Translate element.js script which
 *    renders a <select class="goog-te-combo"> inside #google_translate_element.
 * 2. When the user picks a language from our custom dropdown we:
 *    a. write the googtrans cookie  (/en/<code>)
 *    b. set the <select> value and dispatch a 'change' event
 * 3. Google Translate intercepts the event and translates the DOM in-place.
 * 4. On SPA route changes we re-trigger translation for new DOM nodes.
 */

// ---------------------------------------------------------------------------
// Language list
// ---------------------------------------------------------------------------
export const GOOGLE_TRANSLATE_LANGUAGES = [
  { code: 'en', label: 'English', nativeLabel: 'English' },
  { code: 'ml', label: 'Malayalam', nativeLabel: 'മലയാളം' },
  { code: 'hi', label: 'Hindi', nativeLabel: 'हिन्दी' },
  { code: 'ta', label: 'Tamil', nativeLabel: 'தமிழ்' },
  { code: 'te', label: 'Telugu', nativeLabel: 'తెలుగు' },
  { code: 'kn', label: 'Kannada', nativeLabel: 'ಕನ್ನಡ' },
  { code: 'bn', label: 'Bengali', nativeLabel: 'বাংলা' },
  { code: 'mr', label: 'Marathi', nativeLabel: 'मराठी' },
  { code: 'gu', label: 'Gujarati', nativeLabel: 'ગુજરাતી' },
  { code: 'pa', label: 'Punjabi', nativeLabel: 'ਪੰਜਾਬੀ' },
  { code: 'sa', label: 'Sanskrit', nativeLabel: 'संस्कृतम्' },
] as const;

export type GoogleTranslateLanguageCode = (typeof GOOGLE_TRANSLATE_LANGUAGES)[number]['code'];

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------
let _currentLang = 'en';

// ---------------------------------------------------------------------------
// Malayalam direct-translation engine
// ---------------------------------------------------------------------------
// Google Translate Element only translates from `pageLanguage` (English).
// Malayalam script text is NOT English, so the widget skips it.  We handle
// these elements ourselves by calling the free Google Translate API directly
// with sl=ml (source=Malayalam) → tl=<target>.
//
// Elements are identified by  lang="ml" + class="notranslate"  so the GT
// widget never touches them and we have full control.
// ---------------------------------------------------------------------------

/** per-target-language cache:  targetLang → Map<originalMl, translatedText> */
const _mlCache = new Map<string, Map<string, string>>();
let _mlObserver: MutationObserver | null = null;
let _mlDebounce: ReturnType<typeof setTimeout> | null = null;

/**
 * Find every `[lang="ml"]` element on the page, translate its text from
 * Malayalam to `targetLang` via the free Google Translate REST endpoint,
 * and replace the text content in-place.  Results are cached so repeated
 * calls (after React re-renders) are instant.
 */
async function translateMalayalamElements(targetLang: string): Promise<void> {
  // Nothing to do when target is English or Malayalam itself
  if (!targetLang || targetLang === 'en' || targetLang === 'ml') {
    // Restore originals if any were translated previously
    document.querySelectorAll<HTMLElement>('[lang="ml"]').forEach(el => {
      const orig = el.getAttribute('data-ml-original');
      if (orig && el.textContent !== orig) el.textContent = orig;
    });
    return;
  }

  const elements = Array.from(document.querySelectorAll<HTMLElement>('[lang="ml"]'));
  if (!elements.length) return;

  // Ensure cache map exists
  if (!_mlCache.has(targetLang)) _mlCache.set(targetLang, new Map());
  const cache = _mlCache.get(targetLang)!;

  // ---- Apply cached translations immediately; collect uncached ones ----
  const uncached: { el: HTMLElement; text: string }[] = [];

  for (const el of elements) {
    // Store the original Malayalam text the very first time we see the element
    if (!el.getAttribute('data-ml-original')) {
      el.setAttribute('data-ml-original', el.textContent?.trim() || '');
    }
    const original = el.getAttribute('data-ml-original') || '';
    if (!original) continue;

    const hit = cache.get(original);
    if (hit) {
      if (el.textContent !== hit) el.textContent = hit;
      continue;
    }
    uncached.push({ el, text: original });
  }

  if (!uncached.length) return;

  // ---- Batch-fetch translations from free GT API ----
  const BATCH = 20; // keep URL length reasonable
  for (let i = 0; i < uncached.length; i += BATCH) {
    const chunk = uncached.slice(i, i + BATCH);
    const query = chunk.map(c => c.text).join('\n');
    try {
      const res = await fetch(
        `https://translate.googleapis.com/translate_a/single?client=gtx&sl=ml&tl=${targetLang}&dt=t&q=${encodeURIComponent(query)}`
      );
      if (!res.ok) continue;
      const data = await res.json();
      // response[0] is an array of [translatedSegment, sourceSegment, …]
      const full: string = ((data[0] as unknown[]) || [])
        .map((seg: unknown) => (seg as string[])[0])
        .join('');
      const parts = full.split('\n');
      chunk.forEach((item, j) => {
        const t = (j < parts.length ? parts[j] : '').trim();
        if (t) {
          cache.set(item.text, t);
          if (item.el.textContent !== t) item.el.textContent = t;
        }
      });
    } catch {
      // Silent fail — keep original Malayalam text
    }
  }
}

/** Start a MutationObserver so that React re-renders (which produce fresh,
 *  untranslated `[lang="ml"]` elements) are translated automatically. */
function _startMlObserver(): void {
  _stopMlObserver();
  if (!_currentLang || _currentLang === 'en' || _currentLang === 'ml') return;

  _mlObserver = new MutationObserver(() => {
    if (_mlDebounce) clearTimeout(_mlDebounce);
    _mlDebounce = setTimeout(() => {
      translateMalayalamElements(_currentLang);
    }, 400);
  });
  _mlObserver.observe(document.body, { childList: true, subtree: true });
}

function _stopMlObserver(): void {
  if (_mlObserver) { _mlObserver.disconnect(); _mlObserver = null; }
  if (_mlDebounce) { clearTimeout(_mlDebounce); _mlDebounce = null; }
}

/** Schedule ML translation after the GT widget has had time to finish. */
function _scheduleMlTranslation(langCode: string, delayMs = 1500): void {
  setTimeout(() => {
    translateMalayalamElements(langCode);
    _startMlObserver();
  }, delayMs);
}

// ---------------------------------------------------------------------------
// Cookie helper
// ---------------------------------------------------------------------------
function setTranslateCookie(langCode: string): void {
  const value = langCode && langCode !== 'en' ? `/en/${langCode}` : '/en/en';
  const host = window.location.hostname;
  // Remove old cookies first
  document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${host}`;
  document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${host}`;
  // Set new
  if (langCode !== 'en') {
    document.cookie = `googtrans=${value}; path=/`;
    document.cookie = `googtrans=${value}; path=/; domain=${host}`;
    document.cookie = `googtrans=${value}; path=/; domain=.${host}`;
  }
}

// ---------------------------------------------------------------------------
// Select-element driver
// ---------------------------------------------------------------------------
function fireComboChange(langCode: string): boolean {
  const combo = document.querySelector<HTMLSelectElement>('.goog-te-combo');
  if (!combo) return false;

  combo.value = langCode === 'en' ? '' : langCode;
  combo.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
  return true;
}

function triggerWithRetry(langCode: string, maxRetries = 50, interval = 100): void {
  // Try immediately first
  if (fireComboChange(langCode)) return;

  let attempt = 0;
  const tick = () => {
    attempt++;
    if (fireComboChange(langCode)) return;
    if (attempt < maxRetries) setTimeout(tick, interval);
  };
  setTimeout(tick, interval);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Switch the page to `langCode`.  Pass 'en' to revert to original English.
 */
export function setGoogleTranslateLanguage(langCode: string): void {
  _currentLang = langCode;
  setTranslateCookie(langCode);

  if (langCode === 'en') {
    _stopMlObserver();
    // Clear all googtrans cookies so the page loads fresh in English
    const host = window.location.hostname;
    document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${host}`;
    document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${host}`;
    // Instant reload — page comes back in original English with no translation
    window.location.reload();
    return;
  }

  // When switching between two non-English languages,
  // Google Translate sometimes ignores the change event if the
  // combo already has a value. Reset first, then set new language.
  const combo = document.querySelector<HTMLSelectElement>('.goog-te-combo');
  if (combo && combo.value && combo.value !== langCode) {
    combo.value = '';
    combo.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
    // Short delay to let GT process the reset, then apply new language
    setTimeout(() => {
      setTranslateCookie(langCode);
      triggerWithRetry(langCode, 30, 100);
    }, 50);
    // Translate Malayalam elements after GT finishes
    _scheduleMlTranslation(langCode, 1800);
    return;
  }

  triggerWithRetry(langCode, 30, 100);
  // Translate Malayalam elements after GT finishes
  _scheduleMlTranslation(langCode);
}

/**
 * Re-apply the current translation after SPA route navigation.
 * New DOM produced by React won't be auto-translated; this re-triggers.
 *
 * Returns a Promise that resolves once Google Translate has finished
 * modifying the DOM (detected via MutationObserver), or after a
 * maximum timeout — whichever comes first.
 */
export function reapplyGoogleTranslate(): Promise<void> {
  if (!_currentLang || _currentLang === 'en') return Promise.resolve();

  return new Promise((resolve) => {
    let resolved = false;
    const done = () => {
      if (resolved) return;
      resolved = true;
      // After GT finishes, translate Malayalam elements via direct API
      translateMalayalamElements(_currentLang);
      _startMlObserver();
      resolve();
    };

    // Safety timeout — never block forever
    const safetyTimer = setTimeout(done, 1200);

    // Watch for Google Translate mutating the DOM (it adds <font> tags
    // and modifies text nodes). Once mutations stop for 150ms we
    // assume translation is complete.
    let debounce: ReturnType<typeof setTimeout> | null = null;
    const observer = new MutationObserver(() => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(() => {
        observer.disconnect();
        clearTimeout(safetyTimer);
        done();
      }, 150);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Fire the combo change in multiple passes
    fireComboChange(_currentLang);
    setTimeout(() => fireComboChange(_currentLang), 30);
    setTimeout(() => {
      triggerWithRetry(_currentLang, 20, 80);
    }, 80);
  });
}

/**
 * Get the language that is currently selected.
 */
export function getCurrentGoogleTranslateLanguage(): string {
  return _currentLang;
}
